import React, { useState, useEffect } from 'react';
import axios from 'axios';

import CredentialTest from '../../components/CredentialTest';

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconKey() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AISettingsPage() {
  const [form, setForm] = useState({
    openai_api_key: '',
    anthropic_api_key: '',
    jira_base_url:  '',
    jira_email:     '',
    aws_region:       '',
    jira_secret_name: '',
    pr_jira_check_enabled: false,
    pr_jira_notify_email:  '',
    gchat_service_account_json: '',
    gchat_delegated_subject:    '',
    pr_import_model:             '',
    pr_import_normalizer_prompt: '',
  });
  const [status, setStatus]     = useState(null);  // { type: 'success'|'error', message }
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [configured, setConfigured] = useState({
    openai: false,
    anthropic: false,
    jira:   false,
    gchat:  false,
    aws:    false,
  });
  // From /import/pr/normalizer-config: the selectable Claude models and the
  // built-in defaults, so a blank override can show what it resolves to.
  const [normalizer, setNormalizer] = useState({ models: [], default_model: '', default_prompt: '' });

  useEffect(() => {
    axios.get('/settings/ai')
      .then(res => {
        const d = res.data;
        setConfigured({ openai: d.openai_configured, anthropic: d.anthropic_configured, jira: d.jira_configured, gchat: d.gchat_configured, aws: d.aws_configured });
        setForm(prev => ({
          ...prev,
          jira_base_url: d.jira_base_url || '',
          jira_email:    d.jira_email    || '',
          aws_region:       d.aws_region       || '',
          jira_secret_name: d.jira_secret_name || '',
          pr_jira_check_enabled: !!d.pr_jira_check_enabled,
          pr_jira_notify_email:  d.pr_jira_notify_email || '',
          gchat_delegated_subject: d.gchat_delegated_subject || '',
          pr_import_model:             d.pr_import_model || '',
          pr_import_normalizer_prompt: d.pr_import_normalizer_prompt || '',
          // Masked values — show placeholder so user knows they're set.
          openai_api_key: d.openai_configured ? '***' : '',
          anthropic_api_key: d.anthropic_configured ? '***' : '',
          gchat_service_account_json: d.gchat_configured ? '***' : '',
        }));
      })
      .catch(() => setStatus({ type: 'error', message: 'Failed to load settings.' }))
      .finally(() => setLoading(false));

    // The model list only enriches one dropdown. Coerce it to an array: a
    // non-array here would throw on .map() during render and blank the whole
    // settings page, taking the Jira and AWS fields down with it.
    axios.get('/import/pr/normalizer-config')
      .then(res => setNormalizer({
        models:         Array.isArray(res.data.models) ? res.data.models : [],
        default_model:  res.data.default_model || '',
        default_prompt: res.data.default_prompt || '',
      }))
      .catch(() => {});
  }, []);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setStatus(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      // The enabled flag is stored as a string ('1'/'0') server-side.
      const payload = { ...form, pr_jira_check_enabled: form.pr_jira_check_enabled ? '1' : '0' };
      await axios.put('/settings/ai', payload);
      setStatus({ type: 'success', message: 'Settings saved successfully.' });
      // Reload to refresh masked display.
      const res = await axios.get('/settings/ai');
      const d = res.data;
      setConfigured({ openai: d.openai_configured, anthropic: d.anthropic_configured, jira: d.jira_configured, gchat: d.gchat_configured, aws: d.aws_configured });
      setForm(prev => ({
        ...prev,
        openai_api_key: d.openai_configured ? '***' : '',
        anthropic_api_key: d.anthropic_configured ? '***' : '',
        jira_base_url:  d.jira_base_url || '',
        jira_email:     d.jira_email    || '',
        aws_region:       d.aws_region       || '',
        jira_secret_name: d.jira_secret_name || '',
        pr_jira_check_enabled: !!d.pr_jira_check_enabled,
        pr_jira_notify_email:  d.pr_jira_notify_email || '',
        gchat_delegated_subject: d.gchat_delegated_subject || '',
        gchat_service_account_json: d.gchat_configured ? '***' : '',
        pr_import_model:             d.pr_import_model || '',
        pr_import_normalizer_prompt: d.pr_import_normalizer_prompt || '',
      }));
    } catch {
      setStatus({ type: 'error', message: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Loading settings…</div>
      </div>
    );
  }

  return (
    <div className="page-content" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <div>
          <h2 className="page-title">AI &amp; Jira Settings</h2>
          <p className="page-subtitle">
            Claude does the generative work (code review, turning Jira tickets into catalogue
            entries); the OpenAI key is used only for embeddings. Jira and Bitbucket tokens are
            held in AWS Secrets Manager, not here.
          </p>
        </div>
      </div>

      {/* Status banner */}
      {status && (
        <div className={`alert alert-${status.type === 'success' ? 'success' : 'error'}`}
          style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
          {status.type === 'success' ? <IconCheck /> : <IconAlert />}
          {status.message}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* ── OpenAI (embeddings) ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconKey />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>OpenAI — embeddings only</h3>
            {configured.openai && (
              <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                <IconCheck /> Configured
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="openai_api_key">
              Embeddings API Key
            </label>
            <input
              id="openai_api_key"
              name="openai_api_key"
              type="password"
              className="form-control"
              value={form.openai_api_key}
              onChange={handleChange}
              placeholder={configured.openai ? 'Leave as *** to keep current key' : 'sk-…'}
              autoComplete="new-password"
            />
            <p className="form-hint">
              Used only to turn feature text into embedding vectors (<code>text-embedding-3-small</code>).
              Those vectors power semantic feature search, the blast-radius feature lookup, and the
              &ldquo;related existing feature&rdquo; suggestions in the PR import wizard. No feature
              text is generated with this key.
            </p>
            <p className="form-hint">
              <strong>Why OpenAI here and not Claude?</strong> Anthropic does not offer an embeddings
              API — Claude models generate text but do not return vectors — so a separate embeddings
              provider is required. Switching providers later means re-embedding every feature, because
              vectors from different models are not comparable. Get a key at{' '}
              <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">
                platform.openai.com/api-keys
              </a>.
            </p>
          </div>
        </section>

        {/* ── Anthropic ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconKey />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Anthropic (Claude)</h3>
            {configured.anthropic && (
              <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                <IconCheck /> Configured
              </span>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="anthropic_api_key">API Key</label>
            <input
              id="anthropic_api_key"
              name="anthropic_api_key"
              type="password"
              className="form-control"
              value={form.anthropic_api_key}
              onChange={handleChange}
              placeholder={configured.anthropic ? 'Leave as *** to keep current key' : 'sk-ant-…'}
              autoComplete="new-password"
            />
            <p className="form-hint">
              Powers the AI Code Review tool and the PR import normaliser below, which turns
              Jira ticket text into catalogue-grade feature entries. Get a key at{' '}
              <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
                console.anthropic.com
              </a>.
            </p>
          </div>
        </section>

        {/* ── PR import normaliser ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconKey />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>PR Import — feature normaliser</h3>
          </div>

          <p className="form-hint" style={{ marginTop: 0 }}>
            When PRs are imported, each Jira ticket is rewritten by Claude into a registry entry:
            the capability the product has, not the task the developer did. A bug becomes the
            expected behaviour under its larger feature; a rewrite becomes the capability of the
            thing rewritten. Entries that still read like a task are flagged for review in the
            wizard. Uses the Anthropic key above.
          </p>

          <div className="form-group">
            <label className="form-label" htmlFor="pr_import_model">Model</label>
            <select
              id="pr_import_model"
              name="pr_import_model"
              className="form-control"
              value={form.pr_import_model}
              onChange={handleChange}
            >
              <option value="">
                Default{normalizer.default_model ? ` (${normalizer.default_model})` : ''}
              </option>
              {normalizer.models.map(m => (
                <option key={m.id} value={m.id}>
                  {m.label || m.id}{m.available === false ? ' (no longer served)' : ''}
                </option>
              ))}
            </select>
            <p className="form-hint">
              Sonnet is the default: one short structured call per ticket. Pick Opus if entries for
              thin tickets come out too generic.
            </p>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="pr_import_normalizer_prompt" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              System prompt
              <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>
                (blank = built-in default)
              </span>
              {form.pr_import_normalizer_prompt && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setForm(prev => ({ ...prev, pr_import_normalizer_prompt: '' }))}
                >
                  Reset to default
                </button>
              )}
              {!form.pr_import_normalizer_prompt && normalizer.default_prompt && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => setForm(prev => ({ ...prev, pr_import_normalizer_prompt: normalizer.default_prompt }))}
                >
                  Copy default to edit
                </button>
              )}
            </label>
            <textarea
              id="pr_import_normalizer_prompt"
              name="pr_import_normalizer_prompt"
              className="form-control"
              rows={form.pr_import_normalizer_prompt ? 14 : 6}
              value={form.pr_import_normalizer_prompt}
              onChange={handleChange}
              placeholder={normalizer.default_prompt || 'Loading default prompt…'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <p className="form-hint">
              The model always answers through a fixed structured schema (kind, major feature, sub
              feature, description, attach-to-existing decision), so the prompt only shapes the
              wording and the rules — not the output format.
            </p>
          </div>
        </section>

        {/* ── AWS Secrets Manager ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconKey />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>AWS Secrets Manager</h3>
          </div>

          <p className="form-hint" style={{ marginTop: 0 }}>
            Jira and Bitbucket tokens are read from Secrets Manager at call time, each from
            its own secret whose plain-text value is the token itself. Access uses the EC2
            instance role — the application holds no AWS access keys — so that role needs{' '}
            <code>secretsmanager:GetSecretValue</code> on both secrets. Bitbucket&apos;s secret
            is named on its provider card under Repositories → Providers.
          </p>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="aws_region">
              Region <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
            </label>
            <input
              id="aws_region"
              name="aws_region"
              type="text"
              className="form-control"
              value={form.aws_region}
              onChange={handleChange}
              placeholder="Leave blank to use the instance's own region"
            />
            <p className="form-hint">
              Applies to both the Jira and Bitbucket secrets.
            </p>
          </div>
        </section>

        {/* ── Jira ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconKey />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Jira</h3>
            {configured.jira && (
              <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                <IconCheck /> Configured
              </span>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="jira_base_url">
              Base URL
            </label>
            <input
              id="jira_base_url"
              name="jira_base_url"
              type="url"
              className="form-control"
              value={form.jira_base_url}
              onChange={handleChange}
              placeholder="https://yourcompany.atlassian.net"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="jira_email">
              Email
            </label>
            <input
              id="jira_email"
              name="jira_email"
              type="email"
              className="form-control"
              value={form.jira_email}
              onChange={handleChange}
              placeholder="you@company.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="jira_secret_name">
              AWS secret name
            </label>
            <input
              id="jira_secret_name"
              name="jira_secret_name"
              type="text"
              className="form-control"
              value={form.jira_secret_name}
              onChange={handleChange}
              placeholder="prod/productfeatures/jira"
            />
            <p className="form-hint">
              Name or ARN of the Secrets Manager secret whose plain-text value is the
              Jira API token. The token is never entered or stored here. Create one at{' '}
              <a href="https://id.atlassian.com/manage-profile/security/api-tokens" target="_blank" rel="noreferrer">
                Atlassian account → Security → API tokens
              </a>
              {' '}— use a classic unscoped token, as a scoped token is rejected on the
              site URL above.
            </p>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <CredentialTest
              endpoint="/settings/aws-secrets/test"
              disabled={!form.jira_secret_name}
              payload={() => ({
                jira_secret_name: form.jira_secret_name,
                aws_region:       form.aws_region,
                jira_base_url:    form.jira_base_url,
                jira_email:       form.jira_email,
              })}
            />
            <p className="form-hint">
              Retrieves the token on the instance role and calls Jira with it. Tests what
              is on screen, so you can check before saving. The token is never displayed.
            </p>
          </div>
        </section>

        {/* ── PR → Jira ticket check ── */}
        <section className="card" style={{ marginBottom: 24, padding: '20px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <IconAlert />
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>PR → Jira Ticket Check</h3>
            {configured.gchat && (
              <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                <IconCheck /> Chat configured
              </span>
            )}
          </div>

          <p className="form-hint" style={{ marginTop: 0 }}>
            During a code-review scan, each PR title is checked for a valid Jira ticket
            key (e.g. <code>EN-1234</code>). If a PR has no key, or references a ticket
            that doesn&apos;t exist, a Google Chat DM is sent to the reviewer below.
            Requires Jira above to be configured. Only pinged once per PR head.
          </p>

          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                name="pr_jira_check_enabled"
                type="checkbox"
                checked={form.pr_jira_check_enabled}
                onChange={handleChange}
                style={{ width: 'auto' }}
              />
              Enable the PR → Jira ticket check
            </label>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="pr_jira_notify_email">
              Notify (Google Chat DM to)
            </label>
            <input
              id="pr_jira_notify_email"
              name="pr_jira_notify_email"
              type="email"
              className="form-control"
              value={form.pr_jira_notify_email}
              onChange={handleChange}
              placeholder="maruthi.janardhan@exotel.com"
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="gchat_delegated_subject">
              Delegated sender (impersonated Workspace user)
            </label>
            <input
              id="gchat_delegated_subject"
              name="gchat_delegated_subject"
              type="email"
              className="form-control"
              value={form.gchat_delegated_subject}
              onChange={handleChange}
              placeholder="bot@exotel.com"
            />
            <p className="form-hint">
              The Workspace user the service account impersonates via domain-wide
              delegation. The DM is sent as this user. Leave blank only if the
              service account is published as a Chat app (bot).
            </p>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" htmlFor="gchat_service_account_json">
              Service account key (JSON)
            </label>
            <textarea
              id="gchat_service_account_json"
              name="gchat_service_account_json"
              className="form-control"
              rows={4}
              value={form.gchat_service_account_json}
              onChange={handleChange}
              placeholder={configured.gchat ? 'Leave as *** to keep current key' : 'Paste the full service-account JSON key'}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            />
            <p className="form-hint">
              Google service-account key with the Chat API enabled and domain-wide
              delegation granted for the <code>chat.messages</code> scope. Masked
              after saving.
            </p>
          </div>
        </section>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
